import {
  Inject,
  Injectable,
  ComponentRef,
  ApplicationRef,
  createComponent,
  DestroyRef,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs';

import { HelpService } from '../core/help.service';
import { HelpPanelContentComponent } from './help-panel-content.component';

@Injectable({
  providedIn: 'root',
})
export class HelpPanelPortalService {
  private panelRef?: ComponentRef<HelpPanelContentComponent>;

  constructor(
    private appRef: ApplicationRef,
    private helpService: HelpService,
    private destroyRef: DestroyRef,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.helpService.activePanelId$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((anchorId) => {
          if (anchorId) {
            this.openPanel(anchorId);
            return;
          }

          this.destroyPanel();
        }),
      )
      .subscribe();
  }

  private openPanel(anchorId: string): void {
    this.destroyPanel();

    const anchor = this.helpService.getAnchor(anchorId);
    if (!anchor) {
      console.warn(
        `[HelpPanelPortalService] Cannot open panel: anchor ${anchorId} not found`,
      );
      this.helpService.closePanel();
      return;
    }

    const componentRef = createComponent(HelpPanelContentComponent, {
      environmentInjector: this.appRef.injector,
    });

    // * set panel content properties
    componentRef.instance.helpKey = anchor.config.key;
    componentRef.instance.title = anchor.config.title;
    componentRef.instance.width = anchor.config.width;
    componentRef.instance.height = anchor.config.height;
    componentRef.instance.minWidth = anchor.config.minWidth;
    componentRef.instance.minHeight = anchor.config.minHeight;
    componentRef.instance.maxWidth = anchor.config.maxWidth;
    componentRef.instance.maxHeight = anchor.config.maxHeight;

    const anchorRect = anchor.element.nativeElement.getBoundingClientRect();
    componentRef.instance.initialTop = `${Math.max(16, anchorRect.bottom + 12)}px`;
    componentRef.instance.initialLeft = `${Math.max(16, anchorRect.left)}px`;

    this.appRef.attachView(componentRef.hostView);
    this.document.body.appendChild(componentRef.location.nativeElement);
    componentRef.changeDetectorRef.detectChanges();

    // * subscribe to panel close event
    componentRef.instance.closed
      .pipe(
        takeUntilDestroyed(componentRef.injector.get(DestroyRef)),
        tap(() => {
          this.helpService.closePanel();
        }),
      )
      .subscribe();

    this.panelRef = componentRef;
  }

  private destroyPanel(): void {
    if (!this.panelRef) {
      return;
    }

    this.appRef.detachView(this.panelRef.hostView);
    this.panelRef.destroy();
    this.panelRef = undefined;
  }
}
